import { Route, Routes } from 'react-router-dom';
import { PermissionGuard } from 'features/auth/guards/PermissionGuard';
import { AbpPolicies } from 'core/constants/AbpPolicies';
import { CapabilityRoute } from 'features/auth/guards/CapabilityRoute';
import { Capability } from 'core/roles';
import { PricingProcess } from 'features/subscription-process/pricing-process/PricingProcess';
import { ProfilingProcess } from 'features/subscription-process/profiling-process/ProfilingProcess';
import { ContractualizationStep } from 'features/subscription-process/contractualization-process/ContractualizationStep';
import { ActorRegistrationPage } from 'features/subscription-process/registration-process/ActorRegistrationPage';
import { BorrowerPricingProcessWrapper } from 'features/subscription-process/borrower-pricing-process/BorrowerPricingProcessWrapper';
import { CenteredDashboardContent } from 'features/dashboard/DashboardContent';
import BorrowerProfilingProcessWrapper from 'features/subscription-process/profiling-process/BorrowerProfilingProcess';
import { SubscriptionProcess } from 'features/subscription-process/SubscriptionProcessWrapper';
import { BranchCode } from 'api/risk';
import { RealEstateRegistrationPage } from 'features/subscription-process/home-insurance/RealEstateRegistrationPage';
import { TenantRegistrationPage } from 'features/subscription-process/home-insurance/TenantRegistrationPage';
import { SubscriptionProcessProvider } from 'features/subscription-process/contexts';
import { BorrowerContractualizationStep } from 'features/subscription-process/contractualization-process/BorrowerContractualizationStep';
import RiskDossierResolver, { BorrowerDossierResolver } from 'features/subscription-process/components/RiskDossierResolver';
import { CoproprietyRegistrationPage } from 'features/subscription-process/coproperty/CoproprietyRegistrationPage';
import { CopropertySurveyPage } from 'features/subscription-process/coproperty/CopropertySurveyPage';
import { CopropertyBuildingsPage } from 'features/subscription-process/coproperty/CopropertyBuildingsPage';
import { CopropertyComplementsPage } from 'features/subscription-process/coproperty/CopropertyComplementsPage';
import { CopropertyClaimsPage } from 'features/subscription-process/coproperty/CopropertyClaimsPage';
import { MriEligibilitySummaryPage } from 'features/subscription-process/coproperty/MriEligibilitySummaryPage';
import { LegalProtectionCompanyStepPage } from 'features/subscription-process/legal-protection/LegalProtectionCompanyStepPage';

const SubscriptionProcessRoutes = () => {
  // Tout le process de souscription (comparateur, tarification, contractualisation,
  // profilage) est réservé aux rôles portant SubscriptionProcessAccess (matrice
  // centrale : Courtier). Les autres — Plateforme & co — sont redirigés, y compris
  // en accès direct par URL.
  return (
    <CapabilityRoute capability={Capability.SubscriptionProcessAccess}>
    <PermissionGuard policy={AbpPolicies.SIMU_READ} fallback="/bord">
      <SubscriptionProcessProvider>
        <Routes>
          {/* Routes « dossier » : résolvent l'étape la plus avancée puis redirigent
              (recherche globale, liens partageables). Hors du wrapper : simple écran
              de résolution. Variante emprunteur : projet + risque (URLs différentes). */}
          <Route path="dossier/:riskId" element={<RiskDossierResolver />} />
          <Route
            path="dossier-emprunteur/:borrowerProjectId/:borrowerId?"
            element={<BorrowerDossierResolver />}
          />
          <Route path="/" element={<SubscriptionProcess />}>
            <Route path="inscription">
              <Route
                path="chomage/:riskId"
                element={
                  <ActorRegistrationPage branchCode={BranchCode.Unemployment} />
                }
              />
              <Route
                path="cbno/:riskId"
                element={
                  <ActorRegistrationPage branchCode={BranchCode.UPI} />
                }
              />
              <Route
                path="gli/:riskId"
                element={
                  <ActorRegistrationPage branchCode={BranchCode.RGI} />
                }
              />

              <Route path="cbno/biens/:riskId" element={
                <RealEstateRegistrationPage branchCode={BranchCode.UPI} />
              } />

              <Route path="gli/biens/:riskId" element={
                <RealEstateRegistrationPage branchCode={BranchCode.RGI} />
              } />

              <Route path="tenants/:riskId" element={
                <TenantRegistrationPage branchCode={BranchCode.RGI} />
              } />

              <Route
                path="mri/:riskId"
                element={
                  <ActorRegistrationPage branchCode={BranchCode.Mri} />
                }
              />

              <Route path="mri/copropriete/:riskId" element={
                <CoproprietyRegistrationPage />
              } />

              <Route path="mri/questionnaire/:riskId" element={
                <CopropertySurveyPage />
              } />

              <Route path="mri/batiments/:riskId" element={
                <CopropertyBuildingsPage />
              } />

              <Route path="mri/complements/:riskId" element={
                <CopropertyComplementsPage />
              } />

              <Route path="mri/sinistralite/:riskId" element={
                <CopropertyClaimsPage />
              } />

              <Route path="mri/synthese/:riskId" element={
                <MriEligibilitySummaryPage />
              } />

              <Route
                path="pj/:riskId"
                element={
                  <CenteredDashboardContent>
                    <LegalProtectionCompanyStepPage />
                  </CenteredDashboardContent>
                }
              />
            </Route>

            {/* Routes de profilage */}
            <Route path="profilage">
              <Route path="chomage/:riskId" element={
                <CenteredDashboardContent>
                  <ProfilingProcess />
                </CenteredDashboardContent>
              } />
              <Route path="emprunteur/projet/:borrowerProjectId" element={
                <CenteredDashboardContent>
                  <BorrowerProfilingProcessWrapper />
                </CenteredDashboardContent>
              } />
            </Route>

            {/* Routes de tarification */}
            <Route path="tarification">
              <Route path="emprunteur/:borrowerProjectId/:borrowerId?" element={<BorrowerPricingProcessWrapper />} />
              <Route path=":riskId" element={<PricingProcess />} />
            </Route>

            {/* Routes de contractualisation */}
            <Route path="contractualisation">
              <Route path="borrower/:borrowerProjectId/:borrowerId/:quoteId" element={
                <BorrowerContractualizationStep />
              } />
              <Route path=":riskId/:quoteId?" element={
                <ContractualizationStep />
              } />
            </Route>
          </Route>
        </Routes>
      </SubscriptionProcessProvider>
    </PermissionGuard>
    </CapabilityRoute>
  );
};

export default SubscriptionProcessRoutes;
